import React, { useEffect, useState } from "react";
import { motion, useMotionValue } from "framer-motion";

const baseImgs = [
  { src: "00.jpg", text: "First Image" },
  { src: "01.jpg", text: "Second Image" },
  { src: "03.jpg", text: "Third Image" },
];

const ONE_SECOND = 1000;
const AUTO_DELAY = ONE_SECOND * 3;
const SNAP_DELAY = 750;
const DRAG_BUFFER = 50;

const SPRING_OPTIONS = {
  type: "spring",
  mass: 3,
  stiffness: 400,
  damping: 50,
};

const getPerView = () => {
  if (typeof window === "undefined") return 3;
  const w = window.innerWidth;
  if (w <= 640) return 1;
  if (w <= 768) return 2;
  return 3;
};

export const Slider = () => {
  const [perView, setPerView] = useState(getPerView());
  const [imgIndex, setImgIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const dragX = useMotionValue(0);

  useEffect(() => {
    const onResize = () => setPerView(getPerView());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Append `perView` clones of the first slides so the carousel can keep
  // sliding forward past the last real slide. Once we land in the cloned
  // region (which looks identical to the start), we snap back to index 0
  // without animation, giving a seamless continuous flow in one direction.
  const slides = [...baseImgs, ...baseImgs.slice(0, perView)];

  useEffect(() => {
    const intervalRef = setInterval(() => {
      if (dragX.get() !== 0) return;
      setAnimate(true);
      setImgIndex((prev) => (prev >= baseImgs.length ? prev : prev + 1));
    }, AUTO_DELAY);
    return () => clearInterval(intervalRef);
  }, [dragX]);

  useEffect(() => {
    if (imgIndex < baseImgs.length) return;
    const t = setTimeout(() => {
      setAnimate(false);
      setImgIndex(0);
    }, SNAP_DELAY);
    return () => clearTimeout(t);
  }, [imgIndex]);

  useEffect(() => {
    if (animate) return;
    const t = setTimeout(() => setAnimate(true), 50);
    return () => clearTimeout(t);
  }, [animate]);

  const onDragEnd = () => {
    const x = dragX.get();
    if (x <= -DRAG_BUFFER && imgIndex < baseImgs.length) {
      setAnimate(true);
      setImgIndex((prev) => prev + 1);
    } else if (x >= DRAG_BUFFER && imgIndex > 0) {
      setAnimate(true);
      setImgIndex((prev) => prev - 1);
    }
  };

  const isMobile = perView === 1;
  const isTablet = perView === 2;

  return (
    <div
      className={`relative overflow-hidden sm:mt-[15vh] mt-[14vh] slider-container ${
        isMobile ? "mobile" : isTablet ? "tablet" : "desktop"
      }`}
    >
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        style={{ x: dragX }}
        animate={{ translateX: `-${imgIndex * (100 / perView)}%` }}
        transition={animate ? SPRING_OPTIONS : { duration: 0 }}
        onDragEnd={onDragEnd}
        className="flex cursor-grab items-center active:cursor-grabbing"
      >
        {slides.map((img, idx) => (
          <div
            key={idx}
            className={`flex flex-col items-center ${
              isMobile ? "w-full" : isTablet ? "w-1/2" : "w-1/3"
            } h-[650px] shrink-0`}
          >
            <motion.div
              style={{
                backgroundImage: `url(${img.src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              className="aspect-video w-full h-[650px] object-cover"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};
